import React from 'react';

import { useResponsive } from '@garnish/core';

import {
  Sweetpass,
  useSweetpassCheckoutMachine,
  useSweetpassContent,
  useSweetpassPaymentElement,
} from '@order/features-loyalty';

import { AppFooter } from '../../../../components';

// ─────────────────────────────────────────────────────────────────────────────

export const SweetpassCheckoutReady = (props: SweetpassCheckoutReadyProps) => {
  const { state, content } = props;

  const {
    isPurchasing,
    isConfirmingPayment,
    isPaymentDelayed,
    isCancelingPayment,
    stripeFailedToLoad,
    stripeIntentConfig,
    stripeElementConfig,
    purchase,
    cancelPayment,
    setConfirmStripe,
  } = state;

  const { minWidth, currentBreakpoint } = useResponsive();

  // ─── Payment Element ───────────────────────────────────────────────────────

  const { embeddedPaymentElementView, isUpdatingTotal } = useSweetpassPaymentElement({
    amount: SWEETPASS_AMOUNT,
    stripeIntentConfig,
    stripeElementConfig,
    setConfirmStripe,
  });

  const isPurchaseInProgress = isPurchasing || isConfirmingPayment;
  const isPurchaseDisabled = stripeFailedToLoad || isUpdatingTotal;

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <Sweetpass.Checkout.Container title={content.checkoutTitle} footer={footer}>
      <Sweetpass.Checkout.TwoColumnsLayout.Container>
        <Sweetpass.Checkout.TwoColumnsLayout.LeftColumn>
          {currentBreakpoint.isSM ? (
            <Sweetpass.Checkout.Title>{content.checkoutTitle}</Sweetpass.Checkout.Title>
          ) : null}

          <Sweetpass.Ticket />
        </Sweetpass.Checkout.TwoColumnsLayout.LeftColumn>

        <Sweetpass.Checkout.TwoColumnsLayout.RightColumn>
          {minWidth.isMD ? (
            <Sweetpass.Checkout.Title>{content.checkoutTitle}</Sweetpass.Checkout.Title>
          ) : null}

          <Sweetpass.Checkout.InfoContainer>
            {minWidth.isSM ? null : <Sweetpass.Checkout.InfoContainer.Divider />}

            <Sweetpass.Checkout.HowItWorks howItWorks={content.checkoutHowItWorks} />

            <Sweetpass.Checkout.InfoContainer.Divider />

            {isConfirmingPayment ? (
              <Sweetpass.Checkout.ConfirmingPayment
                isDelayed={isPaymentDelayed}
                isCanceling={isCancelingPayment}
                onCancelPress={cancelPayment}
              />
            ) : (
              <Sweetpass.Checkout.PaymentMethod
                embeddedPaymentElementView={embeddedPaymentElementView}
                hasStripeFailedToLoad={stripeFailedToLoad}
              />
            )}

            {minWidth.isSM ? null : <Sweetpass.Checkout.InfoContainer.Divider />}
          </Sweetpass.Checkout.InfoContainer>

          <Sweetpass.Checkout.Total total={SWEETPASS_DISPLAY_TOTAL} />

          <Sweetpass.Checkout.PurchaseCta
            label={content.checkoutPurchaseCta}
            isLoading={isPurchaseInProgress}
            disabled={isPurchaseDisabled}
            onPress={purchase}
          />
        </Sweetpass.Checkout.TwoColumnsLayout.RightColumn>
      </Sweetpass.Checkout.TwoColumnsLayout.Container>
    </Sweetpass.Checkout.Container>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const footer = <AppFooter />;
const SWEETPASS_AMOUNT = 1000;
const SWEETPASS_DISPLAY_TOTAL = SWEETPASS_AMOUNT / 100;

// ─── Types ───────────────────────────────────────────────────────────────────

type SweetpassCheckoutReadyProps = {
  content: ReturnType<typeof useSweetpassContent>;
  state: ReturnType<typeof useSweetpassCheckoutMachine>;
};
